import type { DataTableFilterField } from '@pesto/ui/components/DataTable/DataTable.types';
import type { ColumnDef, TableState } from '@tanstack/react-table';

import { Typography } from '@pesto/ui';
import { DataTableColumnHeader } from '@pesto/ui/components/DataTable/DataTableColumnHeader';
import {
  generateSelectableFilterOptions,
  setColumnFilterAndSortKey,
} from '@pesto/ui/features/SmartTable/utils/helpers';
import { formatDate } from '@pesto/utils';

import { SecurityEventType, type SecurityEvent } from '../../../../__generated__/graphql/api';

const columnIdNames = {
  insertedAt: setColumnFilterAndSortKey('', 'insertedAtSortDirection'),
  userId: setColumnFilterAndSortKey('userIdSearch', 'userIdSortDirection'),
  type: setColumnFilterAndSortKey('typeSelections', 'typeSortDirection'),
};

export const useSecurityEventsListConfig = () => {
  const securityEventsFilterFields: DataTableFilterField<any>[] = [
    {
      label: 'User ID',
      value: columnIdNames.userId,
      placeholder: 'Search by User ID',
    },
    {
      label: 'Type',
      value: columnIdNames.type,
      options: generateSelectableFilterOptions(SecurityEventType),
    },
  ];

  const getSecurityEventsColumns = (): ColumnDef<SecurityEvent>[] => [
    {
      id: columnIdNames.insertedAt,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      accessorFn: (row) => row.insertedAt,
      cell: ({ row }) => (
        <Typography variant={'body'} className={'w-auto text-nowrap'}>
          {formatDate(row.original.insertedAt)}
        </Typography>
      ),
    },
    {
      id: columnIdNames.type,
      header: 'Type',
      accessorFn: (row) => row.type,
      cell: ({ row }) => row.original.type,
    },
    {
      id: 'description',
      header: 'Description',
      accessorFn: (row) => row.description,
      cell: ({ row }) => row.original.description,
    },
    {
      id: 'userFirstName',
      header: 'First Name',
      accessorFn: (row) => row.user?.firstName,
      cell: ({ row }) => row.original.user?.firstName,
    },
    {
      id: 'userLastName',
      header: 'Last Name',
      accessorFn: (row) => row.user?.lastName,
      cell: ({ row }) => row.original.user?.lastName,
    },
    {
      id: 'userEmail',
      header: 'Email',
      accessorFn: (row) => row.user?.email,
      cell: ({ row }) => row.original.user?.email,
    },
    {
      id: columnIdNames.userId,
      header: 'User ID',
      accessorFn: (row) => row.user?.id,
      cell: ({ row }) => row.original.user?.id,
    },
  ];

  const initialTableState: Partial<TableState> = {
    columnPinning: { left: ['view'] },
    sorting: [{ id: 'insertedAtSortDirection', desc: true }],
  };

  return { securityEventsFilterFields, getSecurityEventsColumns, initialTableState };
};
